import { useLocation, WindowLocation } from '@reach/router';
import { navigate } from 'gatsby';
import styled, { css } from 'styled-components';
import { isNil } from 'ramda';
import React, { useState } from 'react';
import Button from '~/components/Button';
import CopyCode from '~/components/CopyCode';
import GoToPath from '~/components/GoToPath';
import Input from '~/components/Input';
import JustificationContainer from '~/components/JustificationContainer';
import Screenshot from '~/components/Screenshot';
import TextButton from '~/components/TextButton';
import { Body } from '~/components/Typography/index';
import { UpdateRealworksAppStatus_AddTokenContainer__Input } from '~/graphql/types';
import AppDetailsContainer from '~/scenes/Apps/components/AppDetailsContainer';
import AppEntryItemHeader from '~/scenes/Apps/components/AppEntryItemHeader';
import { LINK_PREFIX, StepProps } from '../..';
import RealworksHelpBlock from '../RealworksHelpBlock';
import TEST_ID from './index.testid';
import { isNonEmptyString } from '~/util/Validation/String';
import RealworksCallToActionBlock from '../RealworksCallToActionBlock';

export const importContactsScreenshotLink =
  'https://dathuis-stash.imgix.net/realworks_api_manager.png';

const text = {
  header: 'Contacten importeren',
  description:
    'Om contacten te importeren uit RealWorks gebruiken we de Relaties API. Vul de API Manager in zoals op de onderstaande screenshot. Het is belangrijk dat de instellingen exact overeenkomen.',
  firstAppEntryHeader: '1. Ga in RealWorks naar',
  copyCodeLabel: '2. Plak ons Developer ID in Realworks',
  thirdAppEntryHeader: '3. Kopieer de “Token” en plak deze in dit veld',
  code: 'eb92a256-b27d-4747-80c4-4d4b45485cc8',
  skipStep: 'Stap overslaan',
  skipStepDescription:
    'Wil je geen contacten uit RealWorks importeren, alleen maar exporteren naar RealWorks?',
};

const ImportContactsStep: React.FC<StepProps> = ({ onCancel }) => {
  const [relatie, setRelatie] =
    useState<UpdateRealworksAppStatus_AddTokenContainer__Input['relatie']>(
      null,
    );

  // @ts-ignore WindowLocation<S = unknown>. S is the type of state but we cannot type it ourselves because the types come from types/reach__router
  const location: WindowLocation<{
    values: UpdateRealworksAppStatus_AddTokenContainer__Input;
  }> = useLocation();

  const locationValues = location?.state?.values || {};

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    void navigate(`${LINK_PREFIX}/export-contacts`, {
      replace: true,
      state: {
        values: {
          ...locationValues,
          relatie,
          wonen: null,
        },
      },
    });
  };

  const skipCurrentStep = () =>
    navigate(`${LINK_PREFIX}/export-contacts`, {
      replace: true,
      state: {
        values: {
          ...locationValues,
          relatie: null,
          wonen: null,
        },
      },
    });

  return (
    <AppDetailsContainer
      header={text.header}
      icon="incomingUser"
      dataTestId={TEST_ID.CONTAINER}
    >
      <Body>{text.description}</Body>
      <RealworksCallToActionBlock
        description={text.skipStepDescription}
        skipCurrentStep={skipCurrentStep}
        dataTestId={TEST_ID.NOTIFICATION_BUTTON}
      />
      <RealworksHelpBlock tipId="import-contacts-step-tip" />

      <form onSubmit={onSubmit} data-testid={TEST_ID.FORM}>
        <StyledAppEntryItemHeader>
          {text.firstAppEntryHeader}
        </StyledAppEntryItemHeader>
        <GoToPath
          path={[
            'Servicedesk',
            'Marketplace',
            "API's",
            'Relatie API',
            'Bestellen',
          ]}
          label="Ga naar marketplace"
          link="https://crm.realworks.nl/servlets/objects/admin.marketplace/overview"
        />

        <JustificationContainer
          justification="start"
          margin={['base', null, null, null]}
        >
          <CopyCode
            label={text.copyCodeLabel}
            code={text.code}
            style={{ width: '100%' }}
          />
        </JustificationContainer>

        <JustificationContainer justification="center" margin={['base', null]}>
          <Screenshot
            src={importContactsScreenshotLink}
            alt="screenshot"
            withoutShadow
          />
        </JustificationContainer>
        <AppEntryItemHeader>{text.thirdAppEntryHeader}</AppEntryItemHeader>
        <Input
          appearance="primary"
          validation={[
            value =>
              value === '' || isNil(value) ? 'Vul een waarde in' : true,
          ]}
          validationIndicator
          value={relatie ?? ''}
          onChange={e => setRelatie(e.target.value)}
          dataTestId={TEST_ID.TOKEN_INPUT}
        />

        <JustificationContainer
          justification="space-between"
          align="center"
          margin={['l', null, null]}
        >
          <TextButton
            label="Afbreken"
            onClick={onCancel}
            withoutPadding
            appearance="danger"
            type="button"
            dataTestId="exit-wizard-button"
          />

          <JustificationContainer justification="start">
            <TextButton
              appearance="primary"
              label={text.skipStep}
              onClick={skipCurrentStep}
              dataTestId={TEST_ID.SKIP_BUTTON}
            />
            <Button
              icon="check"
              label="API instellen"
              type="submit"
              size="medium"
              disabled={!isNonEmptyString(relatie)}
              dataTestId={TEST_ID.NEXT_BUTTON}
            />
          </JustificationContainer>
        </JustificationContainer>
      </form>
    </AppDetailsContainer>
  );
};

const StyledAppEntryItemHeader = styled(AppEntryItemHeader)<{}>(
  ({ theme }) => css`
    margin-top: ${theme.space('xl')};
  `,
);

export default ImportContactsStep;
